import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { apiService } from '../services/ApiService';
import { excelToolExecutor } from '../services/excel/ExcelToolExecutor';
import { ensureWorkbookId, getWorkbookId, writeWorkbookId } from '../services/excel/workbookIdentity';
import { WorkbookIndexer, type IndexState } from '../services/workbook/WorkbookIndexer';
import { ChangeBatcher } from '../services/workbook/changeBatcher';
import type { IdentityAnswer } from '../types/index';

//: One indexer for the pane, not one per mount. A remount (React's development double mount, a hot reload,
//: the chat panel re-created) built a second indexer with its own in-flight guard, and the two started two
//: upload jobs on the same workbook seconds apart. The server supersedes the older one, so the first one's
//: capture was thrown away — 22 seconds of it, on a live 19-sheet model.
let shared: WorkbookIndexer | null = null;
let onId: ((id: string) => void) | undefined;

function sharedIndexer(): WorkbookIndexer {
  shared ??= new WorkbookIndexer({
    api: apiService,
    executor: excelToolExecutor,
    ensureWorkbookId: async () => {
      const id = await ensureWorkbookId();
      onId?.(id);
      return id;
    },
    adoptWorkbookId: async (id: string) => {
      // Best effort, as before: the pane moves to the id even when the file cannot take it now (a cell edit);
      // the next run writes it again.
      await writeWorkbookId(id).catch(() => false);
      onId?.(id);
    },
    writeWorkbookId,
    // Read-only: `probe()` asks what the file already carries without writing a custom property into a
    // document the user has not yet agreed to index.
    readWorkbookId: async () => {
      const id = await getWorkbookId();
      if (id) onId?.(id);
      return id;
    },
  });
  return shared;
}

/** Test seam: drop the pane's indexer so the next hook builds a fresh one. */
export function resetWorkbookIndexer(): void {
  shared = null;
  watching = false;
}

//: The pane listens to the workbook's changes once, for as long as it is loaded, like the indexer it feeds. The
//: listener used to belong to the hook's effect: a remount (React's development double mount, the chat panel
//: re-created) registered it twice and removed the first registration, and removing it took the second one's
//: events with it — from then on no edit in Excel started a catch-up until the pane was reloaded.
let watching = false;

function watchChangesOnce(indexer: WorkbookIndexer): void {
  if (watching) return;
  watching = true;
  const batcher = new ChangeBatcher((sheets) => void indexer.markChanged(sheets));
  const restyled = new ChangeBatcher((sheets) => void indexer.markRestyled(sheets));
  void excelToolExecutor.watchSheetChanges((sheets) => batcher.add(sheets), (sheets) => restyled.add(sheets)).catch((err) => {
    watching = false;   // the next mount tries again
    batcher.dispose();
    restyled.dispose();
    console.warn('[sculpt] workbook change events unavailable', err);
  });
}

/** Looks at the workbook when the chat pane mounts and exposes what it found; `consent` starts the first
 *  index, `refresh` re-indexes. The pane no longer indexes on open: a workbook the server has never seen
 *  stops at `needs_consent` and the user starts it from the intro screen. */
export function useWorkbookIndex(onWorkbookId?: (id: string) => void): IndexState & {
  consent: (model?: string | null) => Promise<void>;
  refresh: () => Promise<void>;
  refreshInBackground: () => Promise<void>;
  /** Read every sheet again, in the background, fingerprints aside (`WorkbookIndexer.reread`). */
  reread: () => Promise<void>;
  recheck: () => Promise<void>;
  /** Wait for the server's pass on the current version, nothing captured; whether it finished
   *  (`WorkbookIndexer.awaitUnderstood`, a turn refused for it). */
  awaitUnderstood: () => Promise<boolean>;
  markWritten: (sheets: string[]) => void;
  setAgentWorking: (working: boolean) => void;
  answerIdentity: (answer: IdentityAnswer) => Promise<void>;
  /** The state as it is now, not as of the last render (for callbacks that awaited a run). */
  snapshot: () => IndexState;
} {
  onId = onWorkbookId;
  const indexer = useMemo(() => sharedIndexer(), []);
  useEffect(() => {
    void indexer.probe();
    // No stop() on unmount: the indexer belongs to the pane, and a remount that stopped the run it is about
    // to join is how the double capture happened in the first place.
  }, [indexer]);
  // The user's changes in Excel start the catch-up as soon as they pause, so the next message finds the index
  // already fresh instead of being refused for it (409 index_stale) and waiting. The fingerprint stays the
  // guarantee: an event missed here is caught when the message is sent.
  useEffect(() => watchChangesOnce(indexer), [indexer]);
  const state = useSyncExternalStore(
    (l) => indexer.subscribe(l),
    () => indexer.getState(),
    () => indexer.getState(),
  );
  // A stable identity: effects depending on `refresh` must not re-run on every index progress update (a new
  // function per render re-triggered the stale-index re-index after each commit, 2026-09-15).
  const consent = useCallback((model?: string | null) => indexer.consent(model ?? null), [indexer]);
  const refresh = useCallback(() => indexer.refresh(), [indexer]);
  const refreshInBackground = useCallback(() => indexer.refreshInBackground(), [indexer]);
  const recheck = useCallback(() => indexer.recheck(), [indexer]);
  const awaitUnderstood = useCallback(() => indexer.awaitUnderstood(), [indexer]);
  const reread = useCallback(() => indexer.reread(), [indexer]);
  const markWritten = useCallback((sheets: string[]) => indexer.markWritten(sheets), [indexer]);
  const setAgentWorking = useCallback((working: boolean) => indexer.setAgentWorking(working), [indexer]);
  const answerIdentity = useCallback((answer: IdentityAnswer) => indexer.answerIdentity(answer), [indexer]);
  const snapshot = useCallback(() => indexer.getState(), [indexer]);
  return { ...state, consent, refresh, refreshInBackground, reread, recheck, awaitUnderstood, markWritten, setAgentWorking, answerIdentity, snapshot };
}
